import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { FAQS, TESTIMONIALS, CASE_STUDIES } from '../mock/mock';
import { Star, Quote, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

export const FAQSection = () => (
  <section id="faq" className="py-16 md:py-24 bg-white">
    <div className="max-w-4xl mx-auto px-4">
      <div className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">FAQ</div>
        <h2 className="font-serif-display text-3xl md:text-5xl text-[#2C2416] leading-tight">Answered with honesty</h2>
      </div>
      <Accordion type="single" collapsible className="w-full">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#EFE3D2]">
            <AccordionTrigger className="text-left font-serif text-lg text-[#2C2416] hover:no-underline hover:text-[#C58B76] py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[#5B4A3A] leading-relaxed pb-5">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const Testimonials = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 350;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="py-16 md:py-24 bg-[#FAF6F1]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between md:items-end gap-3 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">Word of mouth</div>
            <h2 className="font-serif-display text-3xl md:text-5xl text-[#2C2416] leading-tight">Loved by the people who trust us</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 rounded-full border border-[#E4D6C1] flex items-center justify-center text-[#2C2416] hover:bg-[#F4D5C5] transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 rounded-full border border-[#E4D6C1] flex items-center justify-center text-[#2C2416] hover:bg-[#F4D5C5] transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth pb-4"
        >
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="min-w-[280px] sm:min-w-[340px] bg-white border border-[#EFE3D2] rounded-2xl p-6 flex flex-col justify-between shadow-sm">
              <div>
                <div className="flex gap-1 text-[#C9A76B] mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-sm text-[#2C2416] leading-relaxed mb-6">"{t.quote}"</p>
              </div>
              <div className="pt-4 border-t border-[#F0E7DA]">
                <div className="font-medium text-sm text-[#2C2416]">{t.name}</div>
                <div className="text-xs text-[#7A5C4C] mt-0.5">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CaseStudyCard = ({ c }) => {
  return (
    <Link 
      to={`/case-studies/${c.slug}`}
      className="group bg-[#FAF6F1] border border-[#EFE3D2] rounded-2xl p-8 flex flex-col justify-between shadow-sm hover:shadow-xl hover:border-[#C58B76]/50 transition-all duration-300 min-h-[440px]"
    >
      <div>
        <div className="flex items-center justify-between mb-5">
          <span className="text-xs uppercase tracking-[0.24em] text-[#C58B76] font-semibold">
            {c.badge || 'Case Study'}
          </span>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#7A5C4C] px-2.5 py-0.5 rounded-full bg-white border border-[#EFE3D2]">
            Story &rarr;
          </span>
        </div>
        <h4 className="font-serif-display text-xl text-[#2C2416] group-hover:text-[#C58B76] transition-colors leading-snug mb-5">
          {c.title}
        </h4>
        <div className="relative">
          <span className="absolute -top-3 -left-3 text-4xl text-[#F4D5C5] font-serif leading-none">“</span>
          <p className="text-[13px] text-[#5B4A3A] italic leading-relaxed pt-2 pl-4 pr-2">
            {c.quote}
          </p>
        </div>
      </div>
      <div className="text-xs uppercase tracking-widest text-[#C58B76] mt-6 flex items-center justify-between border-t border-[#F0E7DA] pt-4 font-semibold group-hover:text-[#8F4A32]">
        <span className="font-medium text-[10px]">Read Challenge &amp; Solution</span>
        <span className="text-sm font-bold group-hover:translate-x-1.5 transition-transform duration-200">→</span>
      </div>
    </Link>
  );
};

export const CaseStudies = () => (
  <section id="success-stories" className="py-16 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-3 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3 font-semibold">Case Studies</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-[#2C2416] max-w-xl leading-tight">Client success stories</h2>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-[#5B4A3A] max-w-md">A look at how thoughtful gifting moved the needle for brands across industries.</p>
          <Link to="/case-studies" className="text-xs uppercase tracking-widest text-[#C58B76] hover:text-[#8F4A32] font-semibold whitespace-nowrap inline-flex items-center gap-1">
            All Stories <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {CASE_STUDIES.map((c, i) => (
          <CaseStudyCard key={i} c={c} />
        ))}
      </div>
    </div>
  </section>
);
